import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface BrevoSyncJob {
  id: string;
  status: "pending" | "running" | "completed" | "failed";
  job_type: "sync_all" | "sync_users" | "sync_user";
  total_users: number;
  synced_count: number;
  failed_count: number;
  errors: string[];
  started_at: string | null;
  completed_at: string | null;
  started_by: string | null;
  created_at: string;
}

interface SyncResult {
  success: boolean;
  synced?: number;
  failed?: number;
  errors?: string[];
  error?: string;
}

interface StartJobResult {
  jobId: string;
  status: string;
  message: string;
}

export const useBrevoSync = () => {
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeJob, setActiveJob] = useState<BrevoSyncJob | null>(null);
  const [recentJobs, setRecentJobs] = useState<BrevoSyncJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Sync all users - starts background job
  const startSyncAllUsers = async (): Promise<string | null> => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-user-to-brevo", {
        body: { action: "start_sync_all" },
      });

      if (error) {
        toast.error(`Failed to start sync: ${error.message}`);
        return null;
      }

      const result = data as StartJobResult;
      setActiveJobId(result.jobId);
      toast.success("Sync started in background. You can close this page.");
      return result.jobId;
    } catch (error) {
      console.error("Brevo sync error:", error);
      toast.error("Failed to start sync");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Get job status
  const getJobStatus = useCallback(async (jobId: string): Promise<BrevoSyncJob | null> => {
    const { data, error } = await supabase.functions.invoke("sync-user-to-brevo", {
      body: { action: "get_job_status", jobId },
    });

    if (error) {
      console.error("Failed to get job status:", error);
      return null;
    }

    return data as BrevoSyncJob;
  }, []);

  // List recent jobs
  const listRecentJobs = useCallback(async (): Promise<BrevoSyncJob[]> => {
    const { data, error } = await supabase.functions.invoke("sync-user-to-brevo", {
      body: { action: "list_recent_jobs" },
    });

    if (error) {
      console.error("Failed to list jobs:", error);
      return [];
    }

    const jobs = (data as { jobs: BrevoSyncJob[] }).jobs || [];
    setRecentJobs(jobs);
    return jobs;
  }, []);

  // Poll for active job status
  useEffect(() => {
    if (!activeJobId) {
      setActiveJob(null);
      return;
    }

    const pollJobStatus = async () => {
      const job = await getJobStatus(activeJobId);
      if (job) {
        setActiveJob(job);
        
        // If job is complete, stop polling and show notification
        if (job.status === "completed" || job.status === "failed") {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          
          if (job.status === "completed") {
            if (job.failed_count === 0) {
              toast.success(`Sync complete! ${job.synced_count} users synced.`);
            } else {
              toast.warning(`Sync complete: ${job.synced_count} synced, ${job.failed_count} failed`);
            }
          } else {
            toast.error("Sync failed. Check the error details.");
          }
          
          // Refresh recent jobs
          listRecentJobs();
          
          // Clear active job after a delay
          setTimeout(() => {
            setActiveJobId(null);
          }, 5000);
        }
      }
    };

    // Initial poll
    pollJobStatus();

    // Start polling interval
    pollingRef.current = setInterval(pollJobStatus, 2000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [activeJobId, getJobStatus, listRecentJobs]);

  // Check for active/recent jobs on mount
  useEffect(() => {
    const checkExistingJobs = async () => {
      const jobs = await listRecentJobs();
      // If there's a running job, resume polling
      const runningJob = jobs.find(j => j.status === "pending" || j.status === "running");
      if (runningJob) {
        setActiveJobId(runningJob.id);
      }
    };
    
    checkExistingJobs();
  }, [listRecentJobs]);

  const syncAllUsers = async (): Promise<SyncResult> => {
    const jobId = await startSyncAllUsers();
    if (jobId) {
      return { success: true, synced: 0, failed: 0, errors: [] };
    }
    return { success: false, error: "Failed to start background sync" };
  };

  return {
    // Sync all users
    syncAllUsers,
    startSyncAllUsers,
    
    // Job status
    activeJob,
    recentJobs,
    getJobStatus,
    listRecentJobs,
    
    // Loading states
    isLoading,
    isSyncing: isLoading || !!activeJob,
    
    // Progress info
    progress: activeJob ? {
      current: activeJob.synced_count + activeJob.failed_count,
      total: activeJob.total_users,
      status: activeJob.status === "running" 
        ? `Syncing ${activeJob.synced_count + activeJob.failed_count}/${activeJob.total_users}...`
        : activeJob.status === "completed"
        ? `Done! ${activeJob.synced_count} synced`
        : activeJob.status === "failed"
        ? "Sync failed"
        : "Starting..."
    } : { current: 0, total: 0, status: "" },
  };
};
